'use client';

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useAnalytics } from '@/hooks/useAnalytics';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { 
      type: "spring", 
      stiffness: 100, 
      damping: 15,
      duration: 0.6 
    },
  },
};

const buttonVariants = {
  rest: { scale: 1 },
  hover: { 
    scale: 1.05,
    transition: { 
      type: "spring", 
      stiffness: 400, 
      damping: 10 
    }
  },
  tap: { scale: 0.95 }
};

export default function CTA() {
  const { trackClick } = useAnalytics();
  const [isMounted, setIsMounted] = useState(false);
  
  useEffect(() => {
    setIsMounted(true);
  }, []);
  
  const handleDemoClick = () => {
    trackClick('cta_demo_button', 'homepage_cta');
  };

  const handleContactClick = () => {
    trackClick('cta_contact_button', 'homepage_cta');
  };

  return (
    <section className="py-24 relative overflow-hidden bg-futuristic-black">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-r from-futuristic-blue/10 via-futuristic-purple/10 to-futuristic-neon/10 z-0" />
      
      {/* Grid pattern removed as requested */}

      {/* Animated particles */}
      {isMounted && (
        <>
          <motion.div
            className="absolute top-20 left-10 w-40 h-40 rounded-full bg-futuristic-blue opacity-5 blur-xl"
            animate={{
              y: [0, -30, 0],
              x: [0, 20, 0],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-32 h-32 rounded-full bg-futuristic-purple opacity-5 blur-xl"
            animate={{
              y: [0, -40, 0],
              x: [0, -30, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
          <motion.div
            className="absolute top-1/2 right-1/4 w-16 h-16 rounded-lg bg-futuristic-neon opacity-5 blur-xl"
            animate={{
              rotate: [0, 180, 0],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
        </>
      )}

      {/* Glowing orbs */}
      <div className="absolute top-0 left-1/4 w-1/2 h-1/2 bg-futuristic-blue/10 rounded-full blur-[100px] -translate-y-1/2" />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-1/2 bg-futuristic-purple/10 rounded-full blur-[100px] translate-y-1/2" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="max-w-4xl mx-auto"
        >
          {/* Content card with glass effect */}
          <div className="bg-futuristic-dark/40 backdrop-blur-md rounded-2xl border border-white/5 p-10 md:p-14 shadow-xl relative overflow-hidden">
            {/* Gradient border top */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-futuristic-blue via-futuristic-purple to-futuristic-neon" />
            
            {/* Inner glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-futuristic-blue/5 via-transparent to-futuristic-purple/5" />
            
            <div className="relative z-10 text-center">
              <motion.h2
                variants={itemVariants}
                className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-white to-gray-400 bg-clip-text text-transparent"
              >
                Ready to transform your sports management experience?
              </motion.h2>
              
              <motion.p
                variants={itemVariants}
                className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto"
              >
                Join thousands of coaches, teams, and parents who are simplifying their sports management with Sketchplay.
              </motion.p>
              
              <motion.div
                variants={itemVariants}
                className="flex flex-col sm:flex-row justify-center gap-6"
              >
                <motion.div
                  variants={buttonVariants}
                  initial="rest"
                  whileHover="hover"
                  whileTap="tap"
                >
                  <Button
                    variant="futuristic"
                    size="xl"
                    href="/demo"
                    className="text-lg px-12 py-7 rounded-full border-2 border-white/10 text-white hover:border-white/20 hover:bg-white/5 transition-all duration-300 relative overflow-hidden group"
                    trackingName="cta_demo_button"
                    trackingLocation="homepage_cta"
                    onClick={handleDemoClick}
                  >
                    <span className="relative z-10">Get a Demo</span>
                    <motion.div 
                      className="absolute inset-0 -z-0 bg-gradient-to-r from-futuristic-blue via-futuristic-purple to-futuristic-neon opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                      initial={false}
                      animate={{ opacity: 0 }}
                      whileHover={{ opacity: 0.2 }}
                      transition={{ duration: 0.3 }}
                    />
                  </Button>
                </motion.div>
                
                <motion.div
                  variants={buttonVariants}
                  initial="rest"
                  whileHover="hover"
                  whileTap="tap"
                >
                  <Button
                    variant="neon"
                    size="xl"
                    href="/contact"
                    className="text-lg px-12 py-7 rounded-full shadow-neon-glow hover:shadow-[0_0_40px_rgba(0,255,102,0.7)] transition-all duration-300"
                    trackingName="cta_contact_button"
                    trackingLocation="homepage_cta"
                    onClick={handleContactClick}
                  >
                    Contact Us
                  </Button>
                </motion.div>
              </motion.div>
              
              <motion.p
                variants={itemVariants}
                className="mt-8 text-gray-300 text-sm"
              >
                No credit card required. Free consultation.
              </motion.p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
} 